import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/hooks/redux-hooks";
import {
  fetchCallCenters,
  fetchCallCenter,
  selectCallCenters,
  selectCallCentersLoading,
  selectSelectedCallCenter,
  selectSelectedCallCenterLoading,
  selectSelectionReady,
  setSelectedCallCenter,
  setSelectionReady,
} from "@/store/call-centers";
import { TCallCenter } from "@/models/call-centers.model";

const SELECTED_QUERY_PARAM = "selected";

type Abortable = { abort: () => void };

export const useCallCenters = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const callCenters = useAppSelector(selectCallCenters);
  const selectedCallCenter = useAppSelector(selectSelectedCallCenter);
  const selectedCallCenterLoading = useAppSelector(selectSelectedCallCenterLoading);
  const loading = useAppSelector(selectCallCentersLoading);

  const fetchOnePromiseRef = useRef<Abortable | null>(null);
  const fetchListPromiseRef = useRef<Abortable | null>(null);
  const restoredFromUrlRef = useRef(false);
  const selectionReady = useAppSelector(selectSelectionReady);

  useEffect(() => {
    fetchListPromiseRef.current?.abort();
    const promise = dispatch(fetchCallCenters());
    fetchListPromiseRef.current = promise;
    return () => {
      promise.abort();
    };
  }, [dispatch]);

  const updateSelectedQueryParam = useCallback(
    (id: number | null) => {
      const next = new URLSearchParams(searchParams);
      if (id === null) {
        next.delete(SELECTED_QUERY_PARAM);
      } else {
        next.set(SELECTED_QUERY_PARAM, String(id));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const selectCallCenter = useCallback(
    (callCenter: TCallCenter | null) => {
      fetchOnePromiseRef.current?.abort();

      if (!callCenter) {
        dispatch(setSelectedCallCenter(null));
        updateSelectedQueryParam(null);
        return;
      }

      dispatch(setSelectedCallCenter(callCenter));
      updateSelectedQueryParam(callCenter.id);
      const promise = dispatch(fetchCallCenter(callCenter.id));
      fetchOnePromiseRef.current = promise;
    },
    [dispatch, updateSelectedQueryParam]
  );

  // Restore selection from the `?selected=` query param once the list arrives.
  // Falling back to the first call center is handled in the Redux reducer.
  // selectionReady is set to true once this runs so dependents know the final
  // selection is stable and can safely trigger their own data fetches.
  useEffect(() => {
    if (restoredFromUrlRef.current || callCenters.length === 0) return;
    const idParam = searchParams.get(SELECTED_QUERY_PARAM);
    const idFromUrl = idParam ? Number(idParam) : NaN;
    if (Number.isFinite(idFromUrl)) {
      const target = callCenters.find((cc) => cc.id === idFromUrl);
      if (target) {
        selectCallCenter(target);
      } else {
        updateSelectedQueryParam(null);
      }
    }
    restoredFromUrlRef.current = true;
    dispatch(setSelectionReady(true));
  }, [callCenters, searchParams, selectCallCenter, updateSelectedQueryParam, dispatch]);

  return {
    callCenters,
    selectedCallCenter,
    selectedCallCenterLoading,
    loading,
    selectionReady,
    selectCallCenter,
  };
};
